import { useEffect, useState } from "react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { SeoHead } from "@/components/SeoHead";
import { WebPageJsonLd } from "@/components/marketing/WebPageJsonLd";
import { Button } from "@/components/ui/button";
import { clearConsent, getConsent, setConsent, type ConsentValue } from "@/lib/analyticsConsent";

const EFFECTIVE_DATE = "May 25, 2026";

export default function Cookies() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Cookie Policy — Excerptus"
        description="How Excerptus uses cookies. Essential cookies only — no third-party advertising, analytics, or cross-site tracking today."
      />
      <WebPageJsonLd
        name="Cookie Policy — Excerptus"
        description="How Excerptus uses cookies. Essential cookies only today."
        path="/cookies/"
        dateModified="2026-05-25"
      />
      <MarketingNav />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <article className="prose-legal">
          <h1 className="font-serif text-4xl font-semibold text-foreground">Cookie Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">Effective: {EFFECTIVE_DATE}</p>

          <Section title="1. What we use today">
            Excerptus uses a small number of <strong>essential cookies and local-storage entries</strong> needed to
            run the app. We do not currently load any third-party advertising, analytics, or
            cross-site tracking scripts.
          </Section>

          <Section title="2. The categories">
            <ul className="mt-2 list-disc space-y-2 pl-6">
              <li>
                <strong>Authentication / session</strong> — set by our backend so you stay signed in
                between visits. Without these, the app cannot work.
              </li>
              <li>
                <strong>Preferences</strong> — your theme choice (light/dark/system) and similar UI
                state are stored in your browser's local storage so the app remembers them.
              </li>
              <li>
                <strong>Payments</strong> — when you start a checkout or open the billing portal,
                Stripe sets its own cookies on the Stripe-hosted page. Those cookies are governed by{" "}
                <a className="text-primary underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
                  Stripe's privacy policy
                </a>.
              </li>
            </ul>
          </Section>

          <Section title="3. Product analytics (Plausible)">
            We load <strong>Plausible Analytics</strong> (EU-hosted, cookieless, anonymous) to
            understand which features people use so we can improve the app. Plausible sets no
            cookies, never identifies individual users, never tracks across sites, and never sells
            data. Because it is fully anonymous it loads by default — if you'd rather not be
            counted at all, click <strong>Reject</strong> below and we'll suppress event sending
            in your browser.
          </Section>



          <Section title="4. What we still do NOT use">
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li>No advertising cookies.</li>
              <li>No cross-site tracking.</li>
              <li>No social-media tracking pixels.</li>
              <li>No session recording.</li>
            </ul>
          </Section>

          <Section title="5. Manage your choice">
            <ConsentManager />
          </Section>

          <Section title="5. Managing cookies in your browser">
            You can clear cookies and local storage at any time through your browser's settings.
            Doing so will sign you out of Excerptus and reset preferences like your theme.
          </Section>

          <Section title="6. Contact">
            Questions? Email{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">
              accounts@excerptus.io
            </a>.
          </Section>
        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-2 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function ConsentManager() {
  const [current, setCurrent] = useState<ConsentValue | null>(null);

  useEffect(() => {
    setCurrent(getConsent());
  }, []);

  const update = (value: ConsentValue | null) => {
    if (value === null) clearConsent();
    else setConsent(value);
    window.dispatchEvent(new CustomEvent("excerptus:consent-changed", { detail: value }));
    setCurrent(value);
  };

  const label =
    current === "granted"
      ? "Analytics: accepted"
      : current === "denied"
      ? "Analytics: rejected"
      : "Analytics: no choice yet (treated as rejected)";

  return (
    <div className="mt-2 rounded-lg border border-border bg-card p-4">
      <p className="text-sm text-foreground">{label}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => update("granted")} disabled={current === "granted"}>
          Accept analytics
        </Button>
        <Button size="sm" variant="outline" onClick={() => update("denied")} disabled={current === "denied"}>
          Reject
        </Button>
        <Button size="sm" variant="ghost" onClick={() => update(null)}>
          Reset choice
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Changes apply immediately in this tab. You may need to reload other open tabs.
      </p>
    </div>
  );
}

